import { DndProvider } from "react-dnd";
import { useTranslation } from "react-i18next";

import { useState } from "react";

import { CircularProgress } from "@mui/material";
import { faPlus } from "@fortawesome/pro-regular-svg-icons";
import { HTML5Backend } from "react-dnd-html5-backend";

import { Icon } from "@/components";
import { Button } from "@/components/Button/Button";
import DeleteModal from "@/components/Modals/DeleteModal";

import {
	useCreateCompanySizeMutation,
	useDeleteCompanySizeMutation,
} from "@/pages/Private/redux/company-size/company-size.api";

import { ButtonColor } from "@/components/Button/types";

import {
	CompanySize,
	CompanySizeArray,
	CreateCompanySize,
	CreateCompanySizeSchema,
} from "../schema/company-size";
import EditModeCompanySizeItem from "./EditableCompanySizeItem/EditableCompanySizeItem";
import EditableCompanySizeItem from "./EditableCompanySizeItem";

interface EditableCompanySizeListProps {
	data?: CompanySizeArray;
	isLoading: boolean;
	editingCompanySizeId: number;
	setEditingCompanySizeId: (value: number) => void;
}

export default function EditableCompanySizeList({
	data,
	isLoading,
	editingCompanySizeId,
	setEditingCompanySizeId,
}: EditableCompanySizeListProps) {
	const { t } = useTranslation();
	const ts = (key: string, options = {}) => t(`manage-company-size.${key}`, options);
	const [isCreating, setCreating] = useState(0);

	const [deletingCompanySize, setDeletingCompanySize] = useState<CompanySize | null>(null);
	const [createCompanySize, { isLoading: isCompanySizeCreating }] = useCreateCompanySizeMutation();
	const [deleteCompanySize, { isLoading: isCompanySizeDeleting }] = useDeleteCompanySizeMutation();

	const handleCreateCompanySize = async (values: CreateCompanySize) => {
		try {
			await createCompanySize(values).unwrap();
		} catch (error) {
			console.error(error);
		} finally {
			setCreating(0);
		}
	};

	const handleDeleteCompanySize = async () => {
		if (!deletingCompanySize?.id) {
			return;
		}

		try {
			await deleteCompanySize(deletingCompanySize?.id ?? 0).unwrap();
			setDeletingCompanySize(null);
		} catch (err) {
			console.error(err);
		}
	};

	const onEditCompanySize = (id: number) => {
		setEditingCompanySizeId(id);
		setCreating(0);
	};

	const handleCancel = () => {
		setEditingCompanySizeId(-1);
		setCreating(0);
	};

	return (
		<DndProvider backend={HTML5Backend}>
			<div
				className={`w-full border border-primary-gray-lightest p-1 pt-4 rounded-16 space-y-4 bg-[#F7F8FB]`}
			>
				<div className="flex justify-between items-center px-3">
					<h3 className="w-full flex gap-2 text-primary-blue-dark text-[14px] font-medium">
						{ts("company-sizes")}

						<span className="border border-primary-gray-lightest bg-primary-gray-lightest rounded-[4px] w-5 h-5 flex items-center justify-center text-[#777D8D] text-[12px] font-medium">
							{data?.length ?? "0"}
						</span>
					</h3>
					<Button
						className="!min-w-5 !min-h-5 !max-w-5 !max-h-5 !p-0"
						color={ButtonColor.ACTION}
						image={<Icon className="w-3 h-4 text-white" icon={faPlus} />}
						title=""
						type="button"
						onClick={() => {
							setCreating(1);
							if (editingCompanySizeId !== -1) {
								setEditingCompanySizeId(-1);
							}
						}}
					/>
				</div>
				{isLoading ? (
					<div className="w-full flex justify-center py-10">
						<CircularProgress className="!w-12 !h-12" />
					</div>
				) : (
					<div className="space-y-1 relative">
						{isCompanySizeDeleting && (
							<div className="w-full h-full absolute flex justify-center py-12 bg-white opacity-50">
								<CircularProgress className="!w-12 !h-12" />
							</div>
						)}
						{isCreating === 1 && (
							<EditModeCompanySizeItem
								data={{ value: "" }}
								isLoading={isCompanySizeCreating}
								setEditing={handleCancel}
								validationSchema={CreateCompanySizeSchema}
								onSubmit={handleCreateCompanySize}
							/>
						)}
						{data?.length ? (
							data?.map((item) => (
								<EditableCompanySizeItem
									key={item?.id}
									data={item}
									isEditing={editingCompanySizeId === item?.id}
									isEditingInProcess={isCreating !== 0 || editingCompanySizeId !== -1}
									setDeletingCompanySize={setDeletingCompanySize}
									setEditing={onEditCompanySize}
								/>
							))
						) : (
							<p className="text-[16px] text-[#777D8D] text-center py-4">{ts("list-empty")}</p>
						)}

						{isCreating === -1 && (
							<EditModeCompanySizeItem
								data={{ value: "" }}
								isLoading={isCompanySizeCreating}
								setEditing={handleCancel}
								validationSchema={CreateCompanySizeSchema}
								onSubmit={handleCreateCompanySize}
							/>
						)}

						<Button
							className="!text-[14px] !m-3 !mt-4 !w-fit !font-medium leading-6"
							image={<Icon className="text-white w-5 h-5 mr-2" icon={faPlus} />}
							title={ts("add-company-size")}
							type="button"
							onClick={() => {
								setCreating(-1);
								if (editingCompanySizeId !== -1) {
									setEditingCompanySizeId(-1);
								}
							}}
						/>
					</div>
				)}
			</div>
			<DeleteModal
				description={ts("delete-description", { sizeValue: deletingCompanySize?.value })}
				handleClose={() => setDeletingCompanySize(null)}
				handleDelete={handleDeleteCompanySize}
				isLoading={isCompanySizeDeleting}
				isShowing={!!deletingCompanySize}
				title={ts("delete-title")}
			/>
		</DndProvider>
	);
}
